/**
 * Default copy for the AI assistant panel.
 *
 * Used when Contentful has no AI_ASSISTANT_CONTENT entry — same pattern as
 * ANALYTICS_DASHBOARD_CONTENT in AnalyticsDashboard.jsx.
 *
 * The fallback applies to the whole object, so a Contentful entry must be
 * complete: authoring a partial one means the missing keys render blank rather
 * than falling back per key. Leave the entry absent until it is fully written.
 */
export const AI_ASSISTANT_FALLBACK = {
    "launcher": {
        "aria_label": "Open AI assistant",
        "tooltip": "Ask a question"
    },
    "panel": {
        "aria_label": "AI assistant",
        "title": "Ask",
        "history_title": "History"
    },
    "header": {
        "clear": "New chat",
        "clear_aria": "Start a new chat",
        "close_aria": "Close",
        "history_aria": "Conversation history",
        "history_tooltip": "History",
        "back_aria": "Back to conversation",
        "back_tooltip": "Back",
        "connector_aria": "Connector"
    },
    "connector_picker": {
        "heading": "Which connector would you like to ask about?",
        "coming_soon_badge": "Coming soon",
        "coming_soon_short": "Soon",
        "unavailable_tooltip": "{connector} is not connected yet"
    },
    "welcome": {
        "subheading": "Type your question or pick a suggestion below."
    },
    "composer": {
        "placeholder": "Ask a question…",
        "aria_label": "Ask a question",
        "send_aria": "Send",
        "thinking_aria": "Thinking",
        "disclaimer": "Responses are AI-generated from your Adobe Target data and may be inaccurate. Don't share sensitive information."
    },
    "history": {
        "loading": "Loading conversations…",
        "empty": "No saved conversations yet. Ask something and it will appear here.",
        "delete_tooltip": "Delete",
        "delete_aria": "Delete conversation: {title}",
        "messages_suffix": "{count} messages",
        "just_now": "just now",
        "minutes_ago": "{count}m ago",
        "hours_ago": "{count}h ago",
        "days_ago": "{count}d ago"
    },
    "attachments": {
        "download_aria": "Download {filename}",
        "downloading_aria": "Downloading {filename}"
    },
    "errors": {
        "no_answer": "No answer came back for that question. Please try again.",
        "request_failed": "Something went wrong reaching the assistant. Please try again.",
        "download_failed": "Could not download {filename}. Please try again.",
        "history_load_failed": "Could not load your conversations.",
        "history_open_failed": "Could not open that conversation.",
        "history_delete_failed": "Could not delete that conversation."
    },
    "connectors": [
        {
            "id": "adobe_target",
            "label": "Adobe Target",
            "description": "Activities, audiences, offers and reports",
            "available": true,
            "prompts": [
                'List all active A/B tests sorted by most recently modified',
                'Get details for activity id 12345',
                'Update the schedule for A/B activity id 67890 to run from <start_date> to <end_date>',
                'Activate activity 12345',
                'Pause activity 12345',
                'Rename activity 12345 to New_Activity_Name',
                'Remove the VariantA experience from A/B activity 12345'
            ]
        },
        {
            "id": "adobe_analytics",
            "label": "Adobe Analytics",
            "description": "Report suites, dimensions and metrics",
            "available": true,
            "prompts": [
                'List activities reporting to analytics',   
                'Pull the A4T report for my checkout optimization test and summarize the Analytics-side conversion data.',
                'Get the performance report for my <activity id>'
            ]
        },
        {
            "id": "tealium",
            "label": "Tealium",
            "description": "Tags, load rules, extensions and data layer variables",
            "available": true,
            // The Tealium MCP server reads an iQ Tag Management profile: tags,
            // mappings, load rules, extensions, variables and version history.
            // It has no audience or event-stream tools, so prompts about those
            // would come back empty.
            "prompts": [
                'Summarise my Tealium profile',
                'Why is order value not being sent to Meta?',
                'Where is the variable customer_email used?',
                'Show the load rules for the Google Analytics tag',
                'List the extensions that run before load rules',
                'What changed between the last two saved versions?',
                'List down with 1 line description each, the extensions configured in my Tealium IQ profile'
            ]
        },
        {
            "id": "chrome_devtools",
            "label": "Chrome DevTools",
            "description": "Browse and debug any public website",
            "available": true,
            // A headless Chrome on the server opens the URL in the prompt, so
            // every prompt names one. Public sites only; some sites block
            // automated browsers, and the assistant says so when they do.
            "prompts": [
                'https://www.example.com/ — go to the site, click on the careers link and list all the remote jobs',
                'Why are Adobe Target offers not showing on https://www.example.com/?',
                'List the console errors and failed network requests on https://www.example.com/',
                'Check which Tealium tags and data layer values load on https://www.example.com/',
                'Run a Lighthouse audit on https://www.example.com/ and summarise the top issues',
                'Take a mobile screenshot of https://www.example.com/'
            ]
        }
    ]
};

/**
 * Fills {placeholders} in a copy string, so strings carrying a filename, count
 * or title stay editable in Contentful instead of being concatenated in JSX.
 */
export const format = (template, values = {}) =>
    String(template ?? '').replace(/\{(\w+)\}/g, (match, key) =>
        Object.prototype.hasOwnProperty.call(values, key)
            ? String(values[key])
            : match
    );
